<template>
  <div class="gift-card wide-container">
    <div class="gift-card__selection">
      <div class="gift-card__selection__box-a">
        <h1 class="gift-card__selection__title">
          <Lang by-key="gift-card-header-title" />
        </h1>

        <h3 class="gift-card__selection__subtitle">
          <Lang by-key="gift-card-header-subtitle" />
        </h3>

        <div class="gift-card__selection__checklist">
          <CheckListItem variant="secondary">
            <Lang by-key="gift-card-ticks-0-body-0" />
          </CheckListItem>
          <CheckListItem variant="secondary">
            <Lang by-key="gift-card-ticks-0-body-1" />
          </CheckListItem>
          <CheckListItem variant="secondary">
            <Lang by-key="gift-card-ticks-0-body-2" />
          </CheckListItem>
        </div>
      </div>

      <div class="gift-card__selection__box-b">
        <div class="gift-card__selection__selector">
          <div class="gift-card__selection__selector__title">
            <Lang by-key="gift-card-choose-experience" />
          </div>

          <div class="gift-card__selection__selector__exp-list">
            <div
              v-for="(exp, i) in experiences"
              :key="exp.name + i"
              class="gift-card__selection__selector__exp-list__item"
              :class="exp.variant"
              @click="selectExperience(exp)"
            >
              <div class="gift-card__selection__selector__exp-list__item__label">
                <DynLang
                  :by-key="exp.name"
                  capitalize-all
                />
              </div>
              <div class="gift-card__selection__selector__exp-list__item__price">
                {{ exp.price }} €
              </div>
            </div>
          </div>
        </div>

        <div class="gift-card__selection__slider-selector">
          <div class="gift-card__selection__slider-selector__title">
            <Lang
              by-key="select-price"
              capitalize-first
            />
          </div>

          <div>
            <SplSlider
              v-model="cardValue"
              :step="5"
              @input="onCardValueInput"
            />
          </div>

          <div class="gift-card__selection__slider-selector__value-display">
            <p class="gift-card__selection__slider-selector__value-display__value">
              <SplInputNumber
                id="card-value-input"
                v-model="cardValue"
                centered
                :label="cardValueStr"
                @input="onCardValueInput"
              />
            </p>
          </div>
        </div>

        <div class="gift-card__selection__buy-btn">
          <SplNuxtLink
            :to="giftCheckoutLink"
            :query="{ id: cardValue }"
            no-lang
            is-external
          >
            <SplButton
              variant="acent"
              size="lg"
            >
              <Lang
                by-key="continue"
                capitalize-first
              />
            </SplButton>
          </SplNuxtLink>
        </div>
      </div>

      <div class="gift-card__selection__box-c">
        <div>
          <div
            v-if="selectedExperience"
            class="gift-card__selection__current-selection"
            :class="selectedExperience.variant"
          >
            <div class="gift-card__selection__current-selection__label">
              <DynLang
                :by-key="selectedExperience.name"
                capitalize-all
              />
            </div>
            <div class="gift-card__selection__current-selection__price">
              {{ selectedExperience.price }} €
            </div>
          </div>

          <div class="gift-card__selection__checklist">
            <CheckListItem>
              <Lang by-key="gift-card-ticks-1-body-0" />
            </CheckListItem>
            <CheckListItem>
              <Lang by-key="gift-card-ticks-1-body-1" />
            </CheckListItem>
            <CheckListItem>
              <Lang by-key="gift-card-ticks-1-body-2" />
            </CheckListItem>
          </div>

          <div class="gift-card__selection__buy-btn">
            <SplNuxtLink
              :to="giftCheckoutLink"
              :query="{ id: cardValue }"
              no-lang
              is-external
            >
              <SplButton
                variant="acent"
                size="lg"
              >
                <Lang
                  by-key="buy"
                  capitalize-first
                />
              </SplButton>
            </SplNuxtLink>
          </div>
        </div>
      </div>
    </div>

    <div class="gift-card__smol-faq">
      <div class="gift-card__smol-faq__item">
        <div class="gift-card__smol-faq__item__head">
          <Lang by-key="gift-card-mini-faq-header-0" />
        </div>

        <div class="gift-card__smol-faq__item__body">
          <Lang by-key="gift-card-mini-faq-body-1" />
        </div>
        <div class="gift-card__smol-faq__item__body">
          <Lang by-key="gift-card-mini-faq-body-2" />
        </div>
        <div class="gift-card__smol-faq__item__body">
          <Lang by-key="gift-card-mini-faq-body-3" />
        </div>
      </div>

      <div class="gift-card__smol-faq__item">
        <div class="gift-card__smol-faq__item__head">
          <Lang by-key="gift-card-mini-faq-header-1" />
        </div>

        <div class="gift-card__smol-faq__item__body">
          <Lang by-key="gift-card-mini-faq-body-4" />
        </div>
        <div class="gift-card__smol-faq__item__body">
          <Lang by-key="gift-card-mini-faq-body-5" />
        </div>
        <div class="gift-card__smol-faq__item__body">
          <Lang by-key="gift-card-mini-faq-body-6" />
        </div>
        <div class="gift-card__smol-faq__item__body">
          <Lang by-key="gift-card-mini-faq-body-7" />
        </div>
      </div>
    </div>

    <div class="gift-card__faq">
      <div class="gift-card__faq__header">
        <DynLang
          by-key="manage-voucher-faq-header"
          capitalize-first
        />
      </div>

      <div
        v-for="(faqItem, i) in faqItems"
        :key="`faq-${i}`"
        class="gift-card__faq__item"
      >
        <CollapsableSection
          :separator="i < (faqItems.length - 1)"
          :title="faqItem.header"
          :html="faqItem.body"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import SplButton from '~/core/components/shared/form/SplButton.vue'
import SplNuxtLink from '~/core/components/shared/SplNuxtLink.vue'
import SplInputNumber from '~/core/components/shared/form/SplInputNumber.vue'
import CheckListItem from '~/core/components/shared/CheckListItem.vue'
import SplSlider from '~/core/components/shared/form/SplSlider.vue'
import CollapsableSection from '~/core/components/shared/CollapsableSection.vue'

import HeadMetaBuilder from '~/core/ts/util/HeadMetaBuilder'

import { translateByKey } from '~/core/ts/util/translate'
import { Money } from '~/core/ts/entity/Money'
import GiftCardFAQRepository from '~/core/ts/repository/GiftCardFAQRepository'

definePageMeta({
  layout: 'default-no-menu',
})

type Experience = {
  name: string,
  variant: string,
  price: number,
}

export default defineNuxtComponent({
  components: {
    SplButton,
    SplNuxtLink,
    SplInputNumber,
    CheckListItem,
    SplSlider,
    CollapsableSection,
  },
  setup() {
    const experiences: Array<Experience> = [
      {
        name: 'gift-card-1',
        variant: 'light-orange',
        price: 25
      },
      {
        name: 'gift-card-2',
        variant: 'dark-sea',
        price: 50
      },
      {
        name: 'gift-card-3',
        variant: 'light-green',
        price: 80
      },
      {
        name: 'gift-card-4',
        variant: 'dark-orange',
        price: 100
      },
      {
        name: 'gift-card-5',
        variant: 'dark-green',
        price: 120
      },
    ]

    const selectedExperience = ref<Experience>(experiences[2])

    const cardValue = ref<number>(selectedExperience.value.price)
    const cardValueStr = computed(() => {
      const currentValue = Number(cardValue.value)
      return Money.toString(Money.fromAmount(currentValue * 100), 0)
    })

    function selectExperience(exp: Experience) {
      selectedExperience.value = exp
      cardValue.value = exp.price
    }

    function onCardValueInput() {
      selectExperience({
        name: 'gift-card',
        variant: 'light-sea',
        price: cardValue.value,
      })
    }

    const builder = HeadMetaBuilder.create()
    const title = translateByKey('gift-cards')
    builder.withTitleAndMetaTitle(`${title} | Spalopia.com`)
    useHead(builder.get())

    const runtimeConfig = useRuntimeConfig()

    const giftCheckoutLink = computed(() => {
      return `${runtimeConfig.public.urls.static.giftCardCheckout}`
    })

    const faqItems = GiftCardFAQRepository.get()

    const route = useRoute()
    onMounted(() => {
      const value = Number(route.query.id)
      if (value) {
        cardValue.value = value
        onCardValueInput()
      }
    })

    return {
      cardValue,
      cardValueStr,
      selectedExperience,
      experiences,

      selectExperience,

      onCardValueInput,

      giftCheckoutLink,

      faqItems,
    }
  }
})
</script>

<style lang="scss" scoped>
.gift-card {
  @apply pb-6;

  &__selection {
    @apply grid;

    grid-template:
        "a"
        "c"
        "b";

    @screen md {
      @apply grid-cols-2;
      grid-template:
        "a c"
        "b c" auto / 1fr 1fr;
    }

    &__box-a, &__box-b, &__box-c {
      @apply h-full;
      @apply w-full;
    }

    &__box-a {
      grid-area: a;
      @apply px-4;
      @screen md {
        @apply py-6;
        @apply px-8;
      }
    }

    &__box-b {
      grid-area: b;
      @apply px-4;
      @screen md {
        @apply px-8;
      }
    }

    &__box-c {
      grid-area: c;

      @apply py-8;
      @screen md {
        padding-top: 20%;
      }

      @apply bg-spl-gray-1;

      @apply flex;
      @apply justify-center;
      @apply items-start;
    }

    &__title {
      @apply mt-8;
      @apply mb-8;

      @screen md {
        @apply mt-0;
      }

      @apply text-2xl;

      @apply text-spl-primary-dark;
      @apply font-bold;
    }

    &__subtitle {
      @apply my-2;

      @apply text-xl;

      @apply text-spl-dark;
    }

    &__checklist {
      @apply py-8;
    }

    &__buy-btn {
      @apply py-4;

      @apply flex;
      @apply justify-center;
      @apply items-center;
    }

    &__selector {
      @apply py-6;

      &__title {
        @apply font-bold;

        @apply text-spl-dark;
      }

      &__exp-list {
        @apply flex;
        @apply flex-wrap;

        @apply gap-2;

        @apply p-4;

        &__item {
          @apply w-170p;
          @apply h-150p;

          @apply border-2;

          @apply flex;
          @apply flex-col;
          @apply justify-center;
          @apply items-center;

          @apply cursor-pointer;

          &__label, &__price {
            @apply w-9/12;
            @apply text-center;

            @apply font-bold;
          }

          &__price {
            @apply text-xl;
          }
        }

        &__item.active, &__item:hover {
          @apply border-spl-primary-dark;
        }
      }
    }

    &__slider-selector {
      @apply py-6;

      &__title {
        @apply font-bold;

        @apply text-spl-dark;
      }

      &__value-display {
        @apply flex;
        @apply justify-center;

        @apply p-4;
        @apply mb-4;

        &__value {
          @apply bg-white;
          @apply rounded-sm;

          @apply border-1;

          @apply text-lg;
        }
      }
    }

    &__current-selection {
      @apply h-300p;

      @apply border-2;

      @apply font-bold;

      @apply flex;
      @apply flex-col;
      @apply justify-center;
      @apply items-center;

      &__label {
        @apply text-3xl;
      }

      &__price {
        @apply text-4xl;
      }
    }
  }

  &__smol-faq {
    @apply grid;
    @apply grid-rows-2;
    @apply grid-cols-1;

    @apply p-4;

    @screen md {
      @apply p-8;
      @apply gap-12;

      @apply grid-rows-1;
      @apply grid-cols-2;
    }

    &__item {
      @apply py-4;

      @screen md {
        @apply py-2;
      }

      &__head {
        @apply my-4;

        @apply text-lg;
        @apply text-spl-primary-dark;
      }

      &__body {
        @apply mb-1;
      }
    }
  }

  &__faq {
    @apply p-4;
    @screen md {
      @apply p-8;
    }

    &__header {
      @apply text-2xl;

      @apply text-spl-primary-dark;

      @apply text-center;

      @apply py-4;
    }

    &__item {
      @apply mb-4;
    }
  }
}

.light-orange {
  @apply bg-spl-light-orange;
  @apply text-spl-dark-orange;

  @apply border-2;
  @apply border-spl-light-orange;
}

.dark-sea {
  @apply bg-spl-dark-sea;
  @apply text-white;

  @apply border-2;
  @apply border-spl-dark-sea;
}

.light-green {
  @apply bg-spl-light-green;
  @apply text-spl-dark-green;

  @apply border-2;
  @apply border-spl-light-green;
}

.dark-orange {
  @apply bg-spl-dark-orange;
  @apply text-white;

  @apply border-2;
  @apply border-spl-dark-orange;
}

.dark-green {
  @apply bg-spl-dark-green;
  @apply text-white;

  @apply border-2;
  @apply border-spl-dark-green;
}

.light-sea {
  @apply bg-spl-light-sea-70;
  @apply text-spl-white;

  @apply border-2;
  @apply border-spl-light-sea-70;
}
</style>
